import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { apihost } from "../ApiHost";
import { deleteCookie, getCookie } from "../CookieFunctions";

const Header = ({ title = "Insight",Unlock=false,UnlockFun=null }) => {
  const [settingsDropdownOpen, setSettingsDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [credits, setCredits] = useState(null);
  const settingsDropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleSettingsDropdown = (event) => {
    event.stopPropagation();
    setSettingsDropdownOpen(!settingsDropdownOpen);
  };

  // Fetch credits from API
  useEffect(() => {
    const fetchCredits = async () => {
      try {
        const response = await fetch(`${apihost}/validate/credits`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getCookie("_JWT")}`,
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch credits");
        }
        const data = await response.json();
        setCredits(data.credits || 0);
      } catch (error) {
        console.error("Error fetching credits:", error);
      }
    };

    fetchCredits();
  }, [location, navigate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        settingsDropdownRef.current &&
        !settingsDropdownRef.current.contains(event.target)
      ) {
        setSettingsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    deleteCookie("_JWT");
    window.location.href = "/";
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    const id = location.pathname.split("/").pop();
    try {
      const response = await fetch(
        `${apihost}/validate/delete/${id}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getCookie("_JWT")}`,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to delete the item");
      }
      navigate("/");
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
  };

  return (
    <>
      <nav className="w-full bg-white/80 backdrop-blur-md z-50 border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to={'/'}><div className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-indigo-600 text-transparent bg-clip-text">
            {title}
            <span className="text-blue-600">Nest</span>
          </div>
          </Link>

          {/* Credits */}
          <div className="flex items-center gap-4">
            {credits !== null && (
              <div className="flex items-center gap-2 text-sm font-medium">
                <img
                  src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/coin.svg"
                  alt="Credits"
                  className="w-5 h-5"
                />
                {credits}
              </div>
            )}

            {/* Settings and Delete */}
            <div ref={settingsDropdownRef} className="relative">
              {location.pathname.startsWith("/view/") && (
                <button
                  onClick={handleDelete}
                  className="mr-2 px-2 py-1 text-red-600 border border-red-600 rounded-lg hover:bg-red-600 hover:text-white"
                >
                  Delete
                </button>

              )}
              {Unlock && (
                <button
                onClick={UnlockFun}
                className="mr-2 px-2 py-1 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-600 hover:text-white"
              >
                Unlock
              </button>)
              }
              <button
                onClick={toggleSettingsDropdown}
                className="p-2 focus:outline-none"
              >
                <img
                  src="https://cdn.jsdelivr.net/npm/bootstrap-icons/icons/gear.svg"
                  alt="Settings"
                  className="w-5 h-5 text-black"
                />
              </button>

              {settingsDropdownOpen && (
                <div
                  className="absolute right-0 mt-2 w-40 bg-white border border-gray-300 rounded-lg shadow-lg z-10"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Link to={'/plans'}>
                  <button className="block px-4 py-2 w-full text-left hover:bg-gray-100 text-sm">
                    Buy Credits
                  </button>
                  </Link>
                  <button
                    className="block px-4 py-2 w-full text-left hover:bg-gray-100 text-sm"
                    onClick={handleLogout}
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Logout Confirmation Modal */}
      {showLogoutModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h2 className="text-lg font-bold mb-4">Are you sure you want to logout?</h2>
            <div className="flex justify-end gap-4">
              <button
                onClick={cancelLogout}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={confirmLogout}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-80">
            <h2 className="text-lg font-bold mb-4">Are you sure you want to delete?</h2>
            <div className="flex justify-end gap-4">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 bg-gray-300 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
